export const endpoints = {
  seller: {
    auth: {
      switchStore: "/seller/auth/switch-store",
      logout: "/seller/auth/logout",
      me: "/seller/auth/me",
    },
    store: "/seller/store",
    storeSettings: "/seller/store/settings",
    storeShippingRules: "/seller/store/shipping-rules",
    storeShippingRuleById: (id: string) => `/seller/store/shipping-rules/${id}`,
    storeFreeShippingRules: "/seller/store/free-shipping-rules",
    storeFreeShippingRuleById: (id: string) =>
      `/seller/store/free-shipping-rules/${id}`,
    storeGovernorates: "/seller/store/governorates",
    customDomains: "/seller/custom-domains",
    customDomainsConfirm: "/seller/custom-domains/confirm",
    customDomainsRefresh: "/seller/custom-domains/refresh",
    profile: "/seller/profile",
    password: "/seller/password",
    paymentOptions: "/seller/payment-options",
    paymentOptionById: (id: string) => `/seller/payment-options/${id}`,
    categories: "/seller/categories",
    categoryById: (id: string) => `/seller/categories/${id}`,
    products: "/seller/products",
    productById: (id: string) => `/seller/products/${id}`,
    productVariants: (productId: string) =>
      `/seller/products/${productId}/variants`,
    productVariantCosts: (productId: string) =>
      `/seller/products/${productId}/variant-costs`,
    productVariantsBulkDelete: (productId: string) =>
      `/seller/products/${productId}/variants/bulk-delete`,
    productVariant: (productId: string, variantId: string) =>
      `/seller/products/${productId}/variants/${variantId}`,
    productOptionValue: (productId: string, valueId: string) =>
      `/seller/products/${productId}/option-values/${valueId}`,
    productImagesById: (id: string) => `/seller/products/${id}/images`,
    productUploads: "/seller/uploads/products",
    storeLogoUploads: "/seller/uploads/store-logo",
    orders: "/seller/orders",
    ordersSmart: "/seller/orders/smart",
    orderStatusCounts: "/seller/orders/status-counts",
    orderById: (id: string) => `/seller/orders/${id}`,
    orderStatus: (id: string) => `/seller/orders/${id}/status`,
    orderStatusUndo: (id: string) => `/seller/orders/${id}/status/undo`,
    orderDraftConfirm: (id: string) => `/seller/orders/${id}/draft/confirm`,
    orderDraftDiscard: (id: string) => `/seller/orders/${id}/draft/discard`,
    orderProof: (id: string) => `/seller/orders/${id}/proof`,
    orderPaymentTransaction: (id: string) =>
      `/seller/orders/${id}/payment-transaction`,
    orderRefund: (id: string) => `/seller/orders/${id}/refund`,
    orderInvoice: (id: string) => `/seller/orders/${id}/invoice`,
    orderReviewApprove: (id: string) =>
      `/seller/orders/${id}/payment-review/approve`,
    orderReviewReject: (id: string) =>
      `/seller/orders/${id}/payment-review/reject`,
    /** Store-wide gateway transactions (all orders), not the per-order view. */
    transactions: "/seller/transactions",
    transactionById: (id: string) => `/seller/transactions/${id}`,
    storeSubscription: "/seller/store/subscription",
    storePolicyTemplates: "/seller/store/policy-templates",
    onboardingGenerate: "/seller/onboarding/generate",
    onboardingApply: "/seller/onboarding/apply",
    onboardingCategorySuggestions: "/seller/onboarding/category-suggestions",
    checkoutLinks: (storeSlug: string) =>
      `/seller/stores/${storeSlug}/checkout-links`,
    fcmTokens: "/seller/fcm-tokens",
    bostaConnection: "/seller/integrations/bosta",
    bostaConnectionActive: "/seller/integrations/bosta/active",
    bostaWebhookRotate: "/seller/integrations/bosta/webhook/rotate",
    jntConnection: "/seller/integrations/jnt",
    jntConnectionActive: "/seller/integrations/jnt/active",
    // Online payment gateways (paymob | paytabs | kashier | fawry) share one
    // shape; the provider slug selects the adapter on the backend.
    paymentGatewayConnection: (provider: string) =>
      `/seller/integrations/${provider}`,
    paymentGatewayActive: (provider: string) =>
      `/seller/integrations/${provider}/active`,
    orderShipment: (orderId: string) => `/seller/orders/${orderId}/shipment`,
    orderShipmentJnt: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/jnt`,
    orderShipmentOptions: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/options`,
    orderShipmentRefresh: (orderId: string) =>
      `/seller/orders/${orderId}/shipment/refresh`,
    orderShipmentAwb: (orderId: string, lang: "ar" | "en") =>
      `/seller/orders/${orderId}/shipment/awb?lang=${lang}`,
    theme: "/seller/theme",
    themeApplyBrandColors: "/seller/theme/apply-brand-colors",
    themeDraft: "/seller/theme/draft",
    themePublish: "/seller/theme/publish",
    themeDiscard: "/seller/theme/discard",
    themePreviewToken: "/seller/theme/preview-token",
    themeAssets: "/seller/theme/assets",
    themeAssetUploadUrl: "/seller/uploads/theme-asset",
    themePublications: "/seller/theme/publications",
    themePublicationRestore: (id: string) =>
      `/seller/theme/publications/${id}/restore`,
    // Theme gallery (composable themes S4)
    themesGallery: "/seller/themes",
    themeApply: (themeId: string) => `/seller/themes/${themeId}/apply`,
    // Theme version-update nudge (composable themes S6)
    themeUpdateAvailable: "/seller/themes/update-available",
    reviews: "/seller/reviews",
    reviewById: (id: string) => `/seller/reviews/${id}`,
    customers: "/seller/customers",
    customerById: (id: string) => `/seller/customers/${id}`,
    coupons: "/seller/coupons",
    couponById: (id: string) => `/seller/coupons/${id}`,
    roles: "/seller/roles",
    roleById: (id: string) => `/seller/roles/${id}`,
    rolePermissions: "/seller/roles/permissions",
    members: "/seller/members",
    memberById: (id: string) => `/seller/members/${id}`,
    invites: "/seller/invites",
    inviteById: (id: string) => `/seller/invites/${id}`,
    search: "/seller/search",
    /** SQL-aggregated dashboard metrics; takes `?range=24h|7d|30d|90d`. */
    analyticsDashboard: "/seller/analytics/dashboard",
  },
  public: {
    invite: (token: string) => `/public/invite/${token}`,
    acceptInvite: (token: string) => `/public/invite/${token}/accept`,
    storeInvite: (token: string) => `/public/store-invite/${token}`,
    acceptStoreInvite: (token: string) =>
      `/public/store-invite/${token}/accept`,
    sellerAuth: {
      login: "/public/seller/auth/login",
    },
    storeIdentity: (storeSlug: string) => `/public/stores/${storeSlug}`,
    storeCategories: (storeSlug: string) =>
      `/public/stores/${storeSlug}/categories`,
    storeProducts: (storeSlug: string) =>
      `/public/stores/${storeSlug}/products`,
    productByRef: (storeSlug: string, productRef: string) =>
      `/public/stores/${storeSlug}/products/${productRef}`,
    storePaymentOptions: (storeSlug: string) =>
      `/public/stores/${storeSlug}/payment-options`,
    storePolicies: (storeSlug: string) =>
      `/public/stores/${storeSlug}/policies`,
    storeShippingCities: (storeSlug: string) =>
      `/public/stores/${storeSlug}/shipping-cities`,
    createDraftOrder: (storeSlug: string) =>
      `/public/stores/${storeSlug}/orders`,
    confirmDraftOrder: (storeSlug: string, draftOrderId: string) =>
      `/public/stores/${storeSlug}/orders/${draftOrderId}`,
    paymentProofUploads: (storeSlug: string) =>
      `/public/stores/${storeSlug}/payment-proof-uploads`,
    trackOrder: "/public/orders/track",
    trackOrderMagic: "/public/orders/tracking-order",
    verifyOrder: "/public/order-verifications",
  },
} as const;
